import type { ButtonDesignTokens } from '@primeuix/themes/types/button'

export default {
  root: {
    lg: {
      fontSize: '{form.field.lg.font.size}',
      paddingX: '{form.field.lg.padding.x}',
      paddingY: '{form.field.lg.padding.y}',
      iconOnlyWidth: '3.25rem',
    },
    sm: {
      fontSize: '{form.field.sm.font.size}',
      paddingX: '{form.field.sm.padding.x}',
      paddingY: '{form.field.sm.padding.y}',
      iconOnlyWidth: '2.25rem',
    },
    gap: '0.5rem',
    label: {
      fontWeight: '600',
    },
    paddingX: '1rem',
    paddingY: '{form.field.padding.y}',
    badgeSize: '1rem',
    focusRing: {
      style: '{form.field.focus.ring.style}',
      width: '{form.field.focus.ring.width}',
      offset: '{form.field.focus.ring.offset}',
    },
    borderRadius: '{form.field.border.radius}',
    raisedShadow:
      '0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)',
    iconOnlyWidth: '2.75rem',
    transitionDuration: '{form.field.transition.duration}',
    roundedBorderRadius: '2rem',
  },
  colorScheme: {
    dark: {
      link: {
        color: '{primary.color}',
        hoverColor: '{primary.color}',
        activeColor: '{primary.color}',
      },
      root: {
        help: {
          color: '{purple.950}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {purple.400}, transparent 80%)',
          },
          background: '{purple.400}',
          hoverColor: '{purple.950}',
          activeColor: '{purple.950}',
          borderColor: '{purple.400}',
          hoverBackground: '{purple.300}',
          activeBackground: '{purple.200}',
          hoverBorderColor: '{purple.300}',
          activeBorderColor: '{purple.200}',
        },
        info: {
          color: '{sky.950}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {sky.400}, transparent 80%)',
          },
          background: '{sky.400}',
          hoverColor: '{sky.950}',
          activeColor: '{sky.950}',
          borderColor: '{sky.400}',
          hoverBackground: '{sky.300}',
          activeBackground: '{sky.200}',
          hoverBorderColor: '{sky.300}',
          activeBorderColor: '{sky.200}',
        },
        warn: {
          color: '{orange.950}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {orange.400}, transparent 80%)',
          },
          background: '{orange.400}',
          hoverColor: '{orange.950}',
          activeColor: '{orange.950}',
          borderColor: '{orange.400}',
          hoverBackground: '{orange.300}',
          activeBackground: '{orange.200}',
          hoverBorderColor: '{orange.300}',
          activeBorderColor: '{orange.200}',
        },
        danger: {
          color: '{red.950}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {red.400}, transparent 80%)',
          },
          background: '{red.400}',
          hoverColor: '{red.950}',
          activeColor: '{red.950}',
          borderColor: '{red.400}',
          hoverBackground: '{red.300}',
          activeBackground: '{red.200}',
          hoverBorderColor: '{red.300}',
          activeBorderColor: '{red.200}',
        },
        primary: {
          color: '{primary.contrast.color}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {primary.color}, transparent 80%)',
          },
          background: '{primary.color}',
          hoverColor: '{primary.contrast.color}',
          activeColor: '{primary.contrast.color}',
          borderColor: '{primary.color}',
          hoverBackground: '{primary.hover.color}',
          activeBackground: '{primary.active.color}',
          hoverBorderColor: '{primary.hover.color}',
          activeBorderColor: '{primary.active.color}',
        },
        success: {
          color: '{green.950}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {green.400}, transparent 80%)',
          },
          background: '{green.400}',
          hoverColor: '{green.950}',
          activeColor: '{green.950}',
          borderColor: '{green.400}',
          hoverBackground: '{green.300}',
          activeBackground: '{green.200}',
          hoverBorderColor: '{green.300}',
          activeBorderColor: '{green.200}',
        },
        contrast: {
          color: '{surface.950}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {surface.0}, transparent 80%)',
          },
          background: '{surface.0}',
          hoverColor: '{surface.950}',
          activeColor: '{surface.950}',
          borderColor: '{surface.0}',
          hoverBackground: '{surface.100}',
          activeBackground: '{surface.200}',
          hoverBorderColor: '{surface.100}',
          activeBorderColor: '{surface.200}',
        },
        secondary: {
          color: '{surface.300}',
          focusRing: {
            color: 'transparent',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {surface.300}, transparent 80%)',
          },
          background: '{surface.800}',
          hoverColor: '{surface.200}',
          activeColor: '{surface.100}',
          borderColor: '{surface.800}',
          hoverBackground: '{surface.700}',
          activeBackground: '{surface.600}',
          hoverBorderColor: '{surface.700}',
          activeBorderColor: '{surface.600}',
        },
      },
      text: {
        help: {
          color: '{purple.400}',
          hoverBackground: 'color-mix(in srgb, {purple.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {purple.400}, transparent 84%)',
        },
        info: {
          color: '{sky.400}',
          hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
        },
        warn: {
          color: '{orange.400}',
          hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
        },
        plain: {
          color: '{surface.0}',
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
        },
        danger: {
          color: '{red.400}',
          hoverBackground: 'color-mix(in srgb, {red.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {red.400}, transparent 84%)',
        },
        primary: {
          color: '{primary.color}',
          hoverBackground:
            'color-mix(in srgb, {primary.color}, transparent 96%)',
          activeBackground:
            'color-mix(in srgb, {primary.color}, transparent 84%)',
        },
        success: {
          color: '{green.400}',
          hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
        },
        contrast: {
          color: '{surface.0}',
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
        },
        secondary: {
          color: '{surface.300}',
          hoverBackground: '{surface.700}',
          activeBackground: '{surface.600}',
        },
      },
      outlined: {
        help: {
          color: '{purple.400}',
          borderColor: '{purple.700}',
          hoverBackground: 'color-mix(in srgb, {help.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {help.400}, transparent 84%)',
        },
        info: {
          color: '{sky.400}',
          borderColor: '{sky.700}',
          hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
        },
        warn: {
          color: '{orange.400}',
          borderColor: '{orange.700}',
          hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
        },
        plain: {
          color: '{surface.0}',
          borderColor: '{surface.600}',
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
        },
        danger: {
          color: '{red.400}',
          borderColor: '{red.700}',
          hoverBackground: 'color-mix(in srgb, {danger.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {danger.400}, transparent 84%)',
        },
        primary: {
          color: '{primary.color}',
          borderColor: '{primary.700}',
          hoverBackground:
            'color-mix(in srgb, {primary.color}, transparent 96%)',
          activeBackground:
            'color-mix(in srgb, {primary.color}, transparent 84%)',
        },
        success: {
          color: '{green.400}',
          borderColor: '{green.700}',
          hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
        },
        contrast: {
          color: '{surface.0}',
          borderColor: '{surface.500}',
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
        },
        secondary: {
          color: '{surface.400}',
          borderColor: '{surface.700}',
          hoverBackground: 'rgba(255,255,255,0.04)',
          activeBackground: 'rgba(255,255,255,0.16)',
        },
      },
    },
    light: {
      link: {
        color: '{primary.color}',
        hoverColor: '{primary.color}',
        activeColor: '{primary.color}',
      },
      root: {
        help: {
          color: '{surface.700}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {purple.200}',
          },
          background: 'transparent',
          hoverColor: '{surface.800}',
          activeColor: '#ffffff',
          borderColor: 'transparent',
          hoverBackground: '{nash.purple.100}',
          activeBackground: '{nash.purple.100}',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
        },
        info: {
          color: '{primary.500}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {sky.200}',
          },
          background: 'transparent',
          hoverColor: '{primary.600}',
          activeColor: '{primary.600}',
          borderColor: 'transparent',
          hoverBackground: '{nash.purple.100}',
          activeBackground: '{nash.purple.100}',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
        },
        warn: {
          color: '{primary.500}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {orange.200}',
          },
          background: '{surface.0}',
          hoverColor: '{primary.600}',
          activeColor: '{primary.600}',
          borderColor: '{surface.500}',
          hoverBackground: '{purple.100}',
          activeBackground: '{primary.100}',
          hoverBorderColor: '{primary.300}',
          activeBorderColor: '{primary.300}',
        },
        danger: {
          color: '{surface.100}',
          focusRing: {
            color: 'transparent',
            shadow: '0 0 0 0.2rem {red.200}',
          },
          background: '{nash.tigerlily.800}',
          hoverColor: '{surface.100}',
          activeColor: '{surface.100}',
          borderColor: '{nash.tigerlily.800}',
          hoverBackground: '{nash.tigerlily.900}',
          activeBackground: '{nash.tigerlily.800}',
          hoverBorderColor: '{nash.tigerlily.900}',
          activeBorderColor: '{nash.tigerlily.800}',
        },
        primary: {
          color: '{primary.contrast.color}',
          focusRing: {
            color: '{primary.color}',
            shadow: 'none',
          },
          background: '{primary.color}',
          hoverColor: '{primary.contrast.color}',
          activeColor: '{primary.contrast.color}',
          borderColor: '{primary.color}',
          hoverBackground: '{primary.hover.color}',
          activeBackground: '{primary.active.color}',
          hoverBorderColor: '{primary.hover.color}',
          activeBorderColor: '{primary.active.color}',
        },
        success: {
          color: '{surface.0}',
          focusRing: {
            color: 'transparent',
            shadow: 'none',
          },
          background: '{nash.fern.600}',
          hoverColor: '{surface.0}',
          activeColor: '{surface.0}',
          borderColor: '{nash.fern.600}',
          hoverBackground: '{nash.fern.700}',
          activeBackground: '{nash.fern.700}',
          hoverBorderColor: '{nash.fern.700}',
          activeBorderColor: '{nash.fern.700}',
        },
        contrast: {
          color: '{surface.0}',
          focusRing: {
            color: '{surface.950}',
            shadow: 'none',
          },
          background: '{surface.950}',
          hoverColor: '{surface.0}',
          activeColor: '{surface.0}',
          borderColor: '{surface.950}',
          hoverBackground: '{surface.900}',
          activeBackground: '{surface.800}',
          hoverBorderColor: '{surface.900}',
          activeBorderColor: '{surface.800}',
        },
        secondary: {
          color: '{primary.500}',
          focusRing: {
            color: '{surface.600}',
            shadow: 'none',
          },
          background: '{primary.100}',
          hoverColor: '{surface.0}',
          activeColor: '{primary.100}',
          borderColor: '{surface.100}',
          hoverBackground: '{primary.600}',
          activeBackground: '{surface.300}',
          hoverBorderColor: '{primary.600}',
          activeBorderColor: '{surface.300}',
        },
      },
      text: {
        help: {
          color: '{purple.500}',
          hoverBackground: '{purple.50}',
          activeBackground: '{purple.100}',
        },
        info: {
          color: '{sky.500}',
          hoverBackground: '{sky.50}',
          activeBackground: '{sky.100}',
        },
        warn: {
          color: '{orange.500}',
          hoverBackground: '{orange.50}',
          activeBackground: '{orange.100}',
        },
        plain: {
          color: '{surface.700}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
        },
        danger: {
          color: '{red.500}',
          hoverBackground: '{red.50}',
          activeBackground: '{red.100}',
        },
        primary: {
          color: '{primary.color}',
          hoverBackground: '{primary.50}',
          activeBackground: '{primary.100}',
        },
        success: {
          color: '{green.500}',
          hoverBackground: '{green.50}',
          activeBackground: '{green.100}',
        },
        contrast: {
          color: '{surface.950}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
        },
        secondary: {
          color: '{surface.600}',
          hoverBackground: '{surface.100}',
          activeBackground: '{surface.200}',
        },
      },
      outlined: {
        help: {
          color: '{purple.500}',
          borderColor: '{purple.200}',
          hoverBackground: '{purple.50}',
          activeBackground: '{purple.100}',
        },
        info: {
          color: '{sky.500}',
          borderColor: '{sky.200}',
          hoverBackground: '{sky.50}',
          activeBackground: '{sky.100}',
        },
        warn: {
          color: '{orange.500}',
          borderColor: '{orange.200}',
          hoverBackground: '{orange.50}',
          activeBackground: '{orange.100}',
        },
        plain: {
          color: '{surface.700}',
          borderColor: '{surface.200}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
        },
        danger: {
          color: '{red.500}',
          borderColor: '{red.200}',
          hoverBackground: '{red.50}',
          activeBackground: '{red.100}',
        },
        primary: {
          color: '{primary.color}',
          borderColor: '{primary.200}',
          hoverBackground: '{primary.50}',
          activeBackground: '{primary.100}',
        },
        success: {
          color: '{green.500}',
          borderColor: '{green.200}',
          hoverBackground: '{green.50}',
          activeBackground: '{green.100}',
        },
        contrast: {
          color: '{surface.950}',
          borderColor: '{surface.700}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
        },
        secondary: {
          color: '{surface.500}',
          borderColor: '{surface.200}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
        },
      },
    },
  },
} satisfies ButtonDesignTokens
